import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { api, ApiError, clearTokens, getAccessToken, setTokens } from '@/services/api'
import { getAnalyticsVisitorID } from '@/services/track'

// --- Types ---

export interface User {
  id: string
  email: string
  username: string
  role: string
  phone_number?: string
  avatar_url?: string
  full_name?: string | null
  provinsi?: string | null
  kota?: string | null
  /** Terisi begitu pendataan wajib selesai. Null = wajib diarahkan ke /mulai. */
  onboarded_at?: string | null
  /** Akun sudah punya password? Akun lama sudah punya walau belum terdata. */
  has_password?: boolean
  farm_name?: string | null
  farm_type?: string[] | null
  production_target?: ProductionTarget | null
  extra_info?: string | null
}

// Backend menyimpan data farm di kolom `meta` (JSONB) dan mengembalikan `role`
// kadang string (login) kadang objek {id,name} (profile) — normalisasi di satu tempat.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeUser(raw: any): User {
  const meta = raw.meta ?? {}
  return {
    id: raw.id,
    email: raw.email,
    username: raw.username,
    role: typeof raw.role === "string" ? raw.role : raw.role?.name ?? "",
    phone_number: raw.phone_number ?? "",
    avatar_url: raw.avatar_url ?? "",
    full_name: meta.full_name ?? null,
    provinsi: meta.provinsi ?? null,
    kota: meta.kota ?? null,
    onboarded_at: meta.onboarded_at ?? null,
    has_password: raw.has_password ?? false,
    farm_name: meta.farm_name ?? null,
    farm_type: meta.farm_type ?? null,
    production_target: meta.production_target ?? null,
    extra_info: meta.extra_info ?? null,
  }
}

export interface ProductionTarget {
  telur?: number
  doc?: number
  remaja_indukan?: number
  daging?: number
}

export interface OnboardingInput {
  full_name: string
  password: string
  provinsi: string
  kota: string
  materi_harapan: string
}

export interface RegisterInput {
  email: string
  username: string
  password: string
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<User>
  cekEmail: (email: string) => Promise<{ terdaftar: boolean; minta_password: boolean }>
  mintaKode: (email: string) => Promise<void>
  masukDenganKode: (email: string, code: string) => Promise<User>
  simpanOnboarding: (data: OnboardingInput) => Promise<User>
  register: (data: RegisterInput) => Promise<{ email: string; verification_required: boolean }>
  verifyEmail: (email: string, code: string) => Promise<void>
  logout: () => Promise<void>
  updateProfile: (data: Partial<User>) => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// --- Provider ---

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = !!user

  const fetchProfile = useCallback(async () => {
    try {
      const data = await api.get<unknown>('/users/profile')
      setUser(normalizeUser(data))
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        clearTokens()
        setUser(null)
      }
      throw err
    }
  }, [])

  // On mount: check if token exists, fetch profile
  useEffect(() => {
    const token = getAccessToken()
    if (!token) {
      setIsLoading(false)
      return
    }

    fetchProfile()
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false))
  }, [fetchProfile])

  // Sinkronisasi antar-tab: logout/login di tab lain ikut berlaku di sini
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== 'access_token') return
      if (e.newValue === null) {
        setUser(null)
      } else {
        fetchProfile().catch(() => setUser(null))
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [fetchProfile])

  const login = useCallback(async (email: string, password: string) => {
    const data = await api.post<{
      access_token: string
      refresh_token: string
      user: unknown
    }>('/auth/login', {
      email,
      password,
      visitor_id: getAnalyticsVisitorID(),
    })

    setTokens(data.access_token, data.refresh_token)
    const u = normalizeUser(data.user)
    setUser(u)
    // Respons login tidak memuat meta (data farm) — lengkapi dari profil
    // supaya popup onboarding tidak muncul lagi bagi user yang sudah mengisi.
    fetchProfile().catch(() => {})
    return u
  }, [fetchProfile])

  // ── Login berbasis email ───────────────────────────────────────────────────
  const cekEmail = useCallback(async (email: string) => {
    return api.post<{ terdaftar: boolean; minta_password: boolean }>(
      '/auth/email-status', { email },
    )
  }, [])

  const mintaKode = useCallback(async (email: string) => {
    await api.post('/auth/login-code', { email })
  }, [])

  const masukDenganKode = useCallback(async (email: string, code: string) => {
    const data = await api.post<{
      access_token: string
      refresh_token: string
      user: unknown
    }>('/auth/login-code/verify', {
      email,
      code,
      visitor_id: getAnalyticsVisitorID(),
    })
    setTokens(data.access_token, data.refresh_token)
    const u = normalizeUser(data.user)
    setUser(u)
    fetchProfile().catch(() => {})
    return u
  }, [fetchProfile])

  const simpanOnboarding = useCallback(async (data: OnboardingInput) => {
    const raw = await api.post<unknown>('/users/onboarding', data)
    const u = normalizeUser(raw)
    setUser(u)
    return u
  }, [])

  const register = useCallback(async (input: RegisterInput) => {
    const data = await api.post<{
      user_id: string
      email: string
      verification_required: boolean
    }>('/auth/register', {
      ...input,
      visitor_id: getAnalyticsVisitorID(),
    })
    return data
  }, [])

  const verifyEmail = useCallback(async (email: string, code: string) => {
    const data = await api.post<{
      access_token: string
      refresh_token: string
      user: unknown
    }>('/auth/verify-email', {
      email,
      code,
      visitor_id: getAnalyticsVisitorID(),
    })

    setTokens(data.access_token, data.refresh_token)
    setUser(normalizeUser(data.user))
  }, [])

  const logout = useCallback(async () => {
    try {
      const rt = localStorage.getItem('refresh_token')
      if (rt) {
        await api.post('/auth/logout', { refresh_token: rt })
      }
    } catch {
      // ignore
    }
    clearTokens()
    setUser(null)
  }, [])

  const updateProfile = useCallback(async (data: Partial<User>) => {
    // Backend hanya menerima username/phone_number/avatar_url/meta —
    // field farm dibungkus ke meta (di-merge per-key oleh backend).
    const meta: Record<string, unknown> = {}
    if (data.farm_name !== undefined) meta.farm_name = data.farm_name
    if (data.farm_type !== undefined) meta.farm_type = data.farm_type
    if (data.production_target !== undefined) meta.production_target = data.production_target
    if (data.extra_info !== undefined) meta.extra_info = data.extra_info

    const payload: Record<string, unknown> = {}
    if (data.username !== undefined) payload.username = data.username
    if (data.phone_number !== undefined) payload.phone_number = data.phone_number
    if (data.avatar_url !== undefined) payload.avatar_url = data.avatar_url
    if (Object.keys(meta).length > 0) payload.meta = meta

    const updated = await api.patch<unknown>('/users/profile', payload)
    setUser(normalizeUser(updated))
  }, [])

  const refreshUser = useCallback(async () => {
    await fetchProfile()
  }, [fetchProfile])

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        login,
        cekEmail,
        mintaKode,
        masukDenganKode,
        simpanOnboarding,
        register,
        verifyEmail,
        logout,
        updateProfile,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// --- Hook ---

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (ctx === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}
